import React, { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import PostGrid from './components/PostGrid';
import PostDetail from './components/PostDetail';
import MonthNavigation from './components/MonthNavigation';
import NewsletterSignup from './NewsletterSignup';
import { getUniqueMonths } from '../../utils/dateUtils';
import { QuillStyles } from './quillConfig';

// Helper function for generating plain text excerpt for meta descriptions
const generatePlainExcerpt = (htmlContent, maxLength = 160) => {
  if (!htmlContent) return '';
  let text = htmlContent.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  text = text.replace(/\s+/g, ' ');
  if (text.length > maxLength) {
    text = text.substring(0, maxLength - 3).trim() + '...';
  }
  return text;
};

const AiNotes = ({ auth }) => {
  const { slug } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [posts, setPosts] = useState([]);
  const [singlePost, setSinglePost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);

  // Check if we're viewing a single post (slug will be present for detail view, undefined for list view)
  const isDetailView = !!slug && slug !== 'admin';

  // Parse tag from URL on mount/change
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tag = params.get('tag');
    setSelectedTag(tag);
  }, [location.search]);

  useEffect(() => {
    if (isDetailView) {
      fetchSinglePost(slug);
    } else {
      fetchPosts(selectedMonth, selectedTag);
    }
  }, [slug, selectedMonth, selectedTag, isDetailView]);

  const fetchPosts = async (month = null, tag = null) => {
    try {
      setLoading(true);
      setError(null);
      
      let url = '/api/posts';
      const params = new URLSearchParams();
      if (month) params.append('month', month);
      if (tag) params.append('tag', tag);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await fetch(url, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.posts && Array.isArray(data.posts)) {
          setPosts(data.posts);
        } else {
          setPosts([]);
        }
      } else {
        setError('Failed to load posts');
        setPosts([]);
      }
    } catch (err) {
      setError(err.message);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSinglePost = async (postSlug) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`/api/posts/by-slug/${postSlug}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.post) {
          setSinglePost(data.post);
        } else {
          setError('Post not found');
          setSinglePost(null);
        }
      } else if (response.status === 404) {
        setError('Post not found');
        setSinglePost(null);
      } else {
        setError('Failed to load post');
        setSinglePost(null);
      }
    } catch (err) {
      setError(err.message);
      setSinglePost(null);
    } finally {
      setLoading(false);
    }
  };

  const handleMonthSelect = (month) => {
    setSelectedMonth(month);
  };

  const handleTagSelect = (tag) => {
    const params = new URLSearchParams(location.search);
    if (tag) {
      params.set('tag', tag);
    } else {
      params.delete('tag');
    }
    history.push({ pathname: '/ai-notes', search: params.toString() });
  };

  const handleClearFilters = () => {
    setSelectedMonth(null);
    history.push('/ai-notes');
  };

  // Get all unique months from all posts (for month navigation)
  // We need to fetch all posts first to get the full list of months
  const [allPosts, setAllPosts] = useState([]);
  
  useEffect(() => {
    if (!isDetailView) {
      // Fetch all posts (without month filter) to get list of available months
      fetch('/api/posts', { credentials: 'include' })
        .then(res => res.json())
        .then(data => {
          if (data.posts && Array.isArray(data.posts)) {
            setAllPosts(data.posts);
          }
        })
        .catch(err => console.error('Error fetching all posts for month navigation:', err));
    }
  }, [isDetailView]);

  const availableMonths = getUniqueMonths(allPosts);

  // Detail View
  if (isDetailView) {
    const pageTitle = singlePost?.meta_title || singlePost?.title || 'AI Notes';
    const pageDescription = singlePost?.meta_description || generatePlainExcerpt(singlePost?.content) || 'AI Notes by Elliot Koss';
    const pageImage = singlePost?.image?.webp_url || singlePost?.image?.s3_url || 'https://elliotkoss.com/images/elliot-koss.jpeg';
    const pageUrl = `https://elliotkoss.com/ai-notes/${slug}`;
    
    return (
      <div style={{
        background: 'linear-gradient(135deg, rgba(232, 232, 232, 0.92) 0%, rgba(248,250,252,0.95) 100%)',
        minHeight: '100vh',
        padding: '120px 60px 60px 60px',
        margin: '0',
        width: '100%'
      }}>
        <Helmet>
          <title>{pageTitle} | AI Notes - Elliot Koss</title>
          <meta name="description" content={pageDescription} />
          <link rel="canonical" href={pageUrl} />
          <meta property="og:type" content="article" />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={pageDescription} />
          <meta property="og:image" content={pageImage} />
          <meta property="og:url" content={pageUrl} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:site" content="@elliotkoss" />
          <meta name="twitter:creator" content="@elliotkoss" />
          <meta name="twitter:title" content={pageTitle} />
          <meta name="twitter:description" content={pageDescription} />
          <meta name="twitter:image" content={pageImage} />
        </Helmet>
      <div style={{ width: '100%', maxWidth: 'none' }}>
        <Row className="justify-content-center">
          <Col lg={10}>
            <button
              onClick={() => history.push('/ai-notes')}
              style={{
                marginBottom: '2rem',
                padding: '0.5rem 1rem',
                backgroundColor: 'transparent',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                color: '#4a5568',
                fontSize: '0.9rem',
                fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.borderColor = '#667eea';
                e.target.style.color = '#667eea';
              }}
              onMouseOut={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.color = '#4a5568';
              }}
            >
              ← Back to AI Notes
            </button>
            
            <PostDetail 
              post={singlePost} 
              loading={loading} 
              error={error} 
            />
          </Col>
        </Row>
      </div>
      </div>
    );
  }

  // List View
  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(232, 232, 232, 0.92) 0%, rgba(248,250,252,0.95) 100%)',
      minHeight: '100vh',
      padding: '120px 60px 60px 60px',
      margin: '0',
      width: '100%'
    }}>
      <Helmet>
        <title>AI Notes - Elliot Koss</title>
        <meta name="description" content="Regular notes about AI tools, technical insights, and what I'm exploring. Nerdy details that wouldn't make sense for LinkedIn." />
        <link rel="canonical" href="https://elliotkoss.com/ai-notes" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="AI Notes - Elliot Koss" />
        <meta property="og:description" content="Regular notes about AI tools, technical insights, and what I'm exploring. Nerdy details that wouldn't make sense for LinkedIn." />
        <meta property="og:url" content="https://elliotkoss.com/ai-notes" />
        <meta property="og:image" content="https://elliotkoss.com/images/elliot-koss.jpeg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@elliotkoss" />
        <meta name="twitter:creator" content="@elliotkoss" />
        <meta name="twitter:title" content="AI Notes - Elliot Koss" />
        <meta name="twitter:description" content="Regular notes about AI tools, technical insights, and what I'm exploring. Nerdy details that wouldn't make sense for LinkedIn." />
        <meta name="twitter:image" content="https://elliotkoss.com/images/elliot-koss.jpeg" />
      </Helmet>
      <div style={{ width: '100%', maxWidth: 'none' }}>
        <Row className="justify-content-center">
          <Col lg={10}>
            {/* Header Section */}
            <div style={{ marginBottom: '3rem' }}>
              <h1 style={{
                fontSize: '3rem',
                fontWeight: '300',
                color: '#2d3748',
                textAlign: 'center',
                marginBottom: '1rem',
                fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif'
              }}>
                AI Notes
              </h1>
              <p style={{
                fontSize: '1.1rem',
                color: '#718096',
                textAlign: 'center',
                marginBottom: '2rem',
                maxWidth: '700px',
                margin: '0 auto 2rem auto',
                lineHeight: '1.6',
                fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif'
              }}>
                A place where I write regular notes about AI tools and what I'm using. Here you'll find nerdy details and technical insights that wouldn't make sense for LinkedIn. I'll talk about all the tools I'm exploring and working with.
              </p>
              
            </div>

            {/* Month Navigation */}
            {availableMonths.length > 0 && (
              <div style={{ marginBottom: '2rem' }}>
                <MonthNavigation
                  months={availableMonths}
                  selectedMonth={selectedMonth}
                  onMonthSelect={handleMonthSelect}
                />
              </div>
            )}

            {/* Active Filters */}
            {(selectedMonth || selectedTag) && (
              <div style={{ 
                marginBottom: '2rem', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.75rem',
                flexWrap: 'wrap'
              }}>
                <span style={{ color: '#718096', fontSize: '0.9rem' }}>Filtering by:</span>
                {selectedTag && (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '4px 12px',
                    backgroundColor: '#667eea',
                    color: '#fff',
                    borderRadius: '16px',
                    fontSize: '0.85rem'
                  }}>
                    Tag: {selectedTag}
                    <span 
                      onClick={() => handleTagSelect(null)}
                      style={{ cursor: 'pointer', fontWeight: 'bold' }}
                    >
                      ×
                    </span>
                  </span>
                )}
                {selectedMonth && (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '4px 12px',
                    backgroundColor: '#667eea',
                    color: '#fff',
                    borderRadius: '16px',
                    fontSize: '0.85rem'
                  }}>
                    Month: {selectedMonth}
                    <span 
                      onClick={() => handleMonthSelect(null)}
                      style={{ cursor: 'pointer', fontWeight: 'bold' }}
                    >
                      ×
                    </span>
                  </span>
                )}
                <button
                  onClick={handleClearFilters}
                  style={{
                    padding: '4px 12px',
                    backgroundColor: 'transparent',
                    border: '1px solid #e2e8f0',
                    borderRadius: '16px',
                    fontSize: '0.85rem',
                    color: '#718096',
                    cursor: 'pointer'
                  }}
                >
                  Clear all
                </button>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div style={{
                padding: '60px 0',
                textAlign: 'center'
              }}>
                <div className="spinner-border" role="status" style={{ color: '#667eea' }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p style={{
                  marginTop: '1rem',
                  color: '#718096',
                  fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif'
                }}>
                  Loading posts...
                </p>
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <div style={{
                padding: '60px 0',
                textAlign: 'center'
              }}>
                <p style={{
                  color: '#e53e3e',
                  fontSize: '1rem',
                  fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif'
                }}>
                  {error}
                </p>
              </div>
            )}

            {/* Posts Grid */}
            {!loading && !error && posts.length > 0 && (
              <PostGrid posts={posts} />
            )}

            {/* Empty State */}
            {!loading && !error && posts.length === 0 && (
              <div style={{
                padding: '60px 0',
                textAlign: 'center'
              }}>
                <p style={{
                  color: '#718096',
                  fontSize: '1rem',
                  fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif'
                }}>
                  {selectedMonth 
                    ? 'No posts found for the selected month.' 
                    : 'No posts available yet.'}
                </p>
              </div>
            )}

            {/* Newsletter Signup - Bottom of page */}
            {!loading && (
              <div style={{ marginBottom: '4rem' }}>
                <NewsletterSignup />
              </div>
            )}
          </Col>
        </Row>
      </div>
    </div>
  );
};

function mapStateToProps({ auth }) {
  return { auth };
}

export default connect(mapStateToProps)((props) => (
  <>
    <QuillStyles />
    <AiNotes {...props} />
  </>
));

